import { ComponentProps } from "react";
import { SubmitHandler, useForm, Controller } from "react-hook-form";

import { Button } from "@design-system/Button/Button";
import { FieldGroup } from "@design-system/FieldGroup/FieldGroup";
import { Form } from "@design-system/Form/Form";
import { TextField } from "@design-system/TextField/TextField";
import { AutotaskCompanySelect } from "@components/Selects/AutotaskCompanySelect";
import { useAutotaskConnectionQuery } from "@graphql/integrations/integrations.g";

export type ClientFormValues = {
  name: string;
  domain: string;
  email?: string | null;
  autotaskCompanyId?: string | null;
};

type Props = {
  submitText?: string;
  onSubmit: SubmitHandler<ClientFormValues>;
  values?: ClientFormValues;
  errorsAPI?: ComponentProps<typeof Form>["errorsAPI"];
};

export const ClientForm = ({
  submitText = "Submit",
  onSubmit,
  values,
  errorsAPI,
}: Props) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<ClientFormValues>({
    values: {
      name: values?.name ?? "",
      domain: values?.domain ?? "",
      email: values?.email ?? "",
      autotaskCompanyId: values?.autotaskCompanyId ?? "",
    },
  });

  const connectionQuery = useAutotaskConnectionQuery();
  const isAutotaskActive = connectionQuery.data?.autotaskConnection?.isActive;

  return (
    <Form<ClientFormValues>
      onSubmit={handleSubmit(onSubmit)}
      setError={setError}
      errorsAPI={errorsAPI}
    >
      <FieldGroup label="Name" error={errors.name?.message} required>
        <TextField
          type="text"
          {...register("name", {
            required: { value: true, message: "This field is required" },
          })}
        />
      </FieldGroup>

      <FieldGroup label="Domain" error={errors.domain?.message} required>
        <TextField
          type="text"
          {...register("domain", {
            required: { value: true, message: "This field is required" },
          })}
        />
      </FieldGroup>

      <FieldGroup label="Email" error={errors.email?.message}>
        <TextField
          type="email"
          {...register("email")}
        />
      </FieldGroup>

      {isAutotaskActive && (
        <Controller
          name="autotaskCompanyId"
          control={control}
          render={({ field }) => (
            <FieldGroup label="Autotask Company" error={errors.autotaskCompanyId?.message}>
              <AutotaskCompanySelect value={field.value || undefined} onChange={field.onChange} />
            </FieldGroup>
          )}
        />
      )}

      <Button isLoading={isSubmitting} type="submit">
        {submitText}
      </Button>
    </Form>
  );
};
